import { type Page, useInfiniteQuery, useQuery } from '@fv/client-core'
import type {
  CarrierDTO,
  CarrierStatsDTO,
  InterestedAccountDto,
} from '@fv/models/admin'

import { adminFetch } from '@/utils'

export const carrierQueryKeys = {
  all: ['carriers'],
  id: (id?: string) => [...carrierQueryKeys.all, ...(id ? [id] : [])],
  search: (search: string, includeRemoved = false) => [
    ...carrierQueryKeys.all,
    'search',
    search,
    includeRemoved.toString(),
  ],
  users: (id: string) => [...carrierQueryKeys.all, 'users', id],
  stats: (id: string) => [...carrierQueryKeys.all, 'stats', id],
  interestedAccounts: (id: string) => [
    ...carrierQueryKeys.all,
    'interested-accounts',
    id,
  ],
}

export function useSearchCarriers(search: string, includeRemoved = false) {
  return useInfiniteQuery({
    queryKey: carrierQueryKeys.search(search, includeRemoved),
    queryFn: ({ pageParam = '' }) => {
      return adminFetch<Page<CarrierDTO>>(`/carriers`, {
        query: { search, continuationToken: pageParam, includeRemoved },
      })
    },
    initialPageParam: '',
    getNextPageParam: p => p.continuationToken,
    staleTime: Infinity,
  })
}

export function useFetchCarrier(id: string) {
  return useQuery(
    carrierQueryKeys.id(id),
    () => adminFetch<CarrierDTO & { currentPro?: number }>(`/carriers/${id}`),
    {
      staleTime: Infinity,
      enabled: !!id,
    },
  )
}

export function useFetchCarrierStats(id: string) {
  return useQuery(
    carrierQueryKeys.stats(id),
    () => adminFetch<CarrierStatsDTO>(`/carriers/${id}/stats`),
    {
      staleTime: Infinity,
    },
  )
}

export function useFetchInterestedAccounts(carrierId: string) {
  return useQuery(
    carrierQueryKeys.interestedAccounts(carrierId),
    () =>
      adminFetch<InterestedAccountDto[]>(
        `/carriers/${carrierId}/interested-accounts`,
      ),
    {
      staleTime: Infinity,
      enabled: !!carrierId,
    },
  )
}
